import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { reviewsService } from '../services/reviewsService';
import { purchasesService } from '../services/purchasesService';

export default function ReviewForm({ listingId, sellerId, onReviewSubmitted }) {
  const { user } = useAuth();
  const [rating, setRating] = useState(5);
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [hasReviewed, setHasReviewed] = useState(false);
  const [existingReview, setExistingReview] = useState(null);
  const [hasPurchased, setHasPurchased] = useState(false);

  useEffect(() => {
    checkExistingReview();
    checkPurchaseStatus();
  }, [listingId, sellerId, user]);

  const checkExistingReview = async () => {
    if (!user || !listingId || !sellerId) return;
    
    try {
      const hasReview = await reviewsService.hasUserReviewedForListing(user.id, sellerId, listingId);
      setHasReviewed(hasReview);
      
      if (hasReview) {
        // Optionally load the existing review to show/edit it
        // For now, we'll just show that they've already reviewed
      }
    } catch (err) {
      console.error('Error checking existing review:', err);
    }
  };

  const checkPurchaseStatus = async () => {
    if (!user || !listingId) return;
    
    try {
      const purchased = await purchasesService.hasUserBoughtListing(listingId, user.id);
      setHasPurchased(purchased);
    } catch (err) {
      console.error('Error checking purchase status:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user || !listingId || !sellerId) return;

    setLoading(true);
    setError('');

    try {
      const reviewData = {
        reviewer_id: user.id,
        reviewee_id: sellerId,
        listing_id: listingId,
        rating: rating,
        text: text.trim() || null
      };

      await reviewsService.createReview(reviewData);
      
      setText('');
      setRating(5);
      setHasReviewed(true);
      
      if (onReviewSubmitted) {
        onReviewSubmitted();
      }
    } catch (err) {
      console.error('Error submitting review:', err);
      setError('Failed to submit review. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="p-4 bg-slate-50 rounded-xl border border-slate-200">
        <p className="text-slate-600 text-center">Please sign in to leave a review.</p>
      </div>
    );
  }

  if (user.id === sellerId) {
    return (
      <div className="p-4 bg-slate-50 rounded-xl border border-slate-200">
        <p className="text-slate-600 text-center">You cannot review your own listing.</p>
      </div>
    );
  }

  if (!hasPurchased) {
    return (
      <div className="p-4 bg-slate-50 rounded-xl border border-slate-200">
        <p className="text-slate-600 text-center">Only buyers can leave reviews. Purchase this item to review the seller.</p>
      </div>
    );
  }

  if (hasReviewed) {
    return (
      <div className="p-4 bg-green-50 rounded-xl border border-green-200">
        <div className="flex items-center gap-2">
          <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          <p className="text-green-800 font-medium">You've already reviewed this seller!</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white/80 backdrop-blur-sm rounded-xl border border-white/40 p-6">
      <h3 className="text-lg font-semibold text-slate-900 mb-4">Leave a Review</h3>
      
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Rating */}
        <div>
          <label className="form-label">Rating</label>
          <div className="flex gap-1">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                type="button"
                onClick={() => setRating(star)}
                className={`w-8 h-8 transition-colors duration-200 ${
                  star <= rating 
                    ? 'text-yellow-400' 
                    : 'text-slate-300 hover:text-yellow-300'
                }`}
              >
                <svg className="w-full h-full fill-current" viewBox="0 0 24 24">
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                </svg>
              </button>
            ))}
          </div>
          <p className="text-sm text-slate-500 mt-1">
            {rating === 1 && 'Poor'}
            {rating === 2 && 'Fair'}
            {rating === 3 && 'Good'}
            {rating === 4 && 'Very Good'}
            {rating === 5 && 'Excellent'}
          </p>
        </div>

        {/* Review Text */}
        <div>
          <label className="form-label">Review (Optional)</label>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            className="form-input resize-none"
            rows={3}
            placeholder="Share your experience with this seller..."
            maxLength={500}
          />
          <p className="text-xs text-slate-500 mt-1">{text.length}/500 characters</p>
        </div>

        {/* Error Display */}
        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-700 text-sm">{error}</p>
          </div>
        )}

        {/* Submit Button */}
        <button
          type="submit"
          className="btn-primary w-full py-3"
          disabled={loading}
        >
          {loading ? (
            <span className="flex items-center justify-center gap-2">
              <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Submitting...
            </span>
          ) : (
            'Submit Review'
          )}
        </button>
      </form>
    </div>
  );
}
